import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Popover, TextField, InputAdornment } from '@mui/material';
import { Check, ChevronDown, Folder, Search, Shield } from 'lucide-react';

import { setCurrProject, setCurrRole } from '../../redux/reducers';
import { selectUser } from '../../redux/selectors';
import type { Project, Role, User } from 'types';

const PROJECT_STORAGE_KEY = 'current_project_id';

type PillProps = {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  active: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};

const Pill = ({ icon, label, value, active, onClick, disabled }: PillProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      height: 32,
      maxWidth: 220,
      padding: '0 10px',
      borderRadius: 'var(--shell-radius-md)',
      border: '1px solid var(--shell-border-subtle)',
      background: active ? 'var(--shell-bg-hover)' : 'var(--shell-bg-surface)',
      color: 'var(--shell-text-primary)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      fontSize: 13,
      fontWeight: 500,
      transition: 'background var(--shell-transition-fast)',
    }}
    onMouseEnter={(e) => {
      if (!disabled && !active)
        (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-hover)';
    }}
    onMouseLeave={(e) => {
      if (!disabled && !active)
        (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-surface)';
    }}
  >
    <span
      style={{
        display: 'inline-flex',
        color: 'var(--shell-text-tertiary)',
        flexShrink: 0,
      }}
    >
      {icon}
    </span>
    <span
      style={{
        color: 'var(--shell-text-tertiary)',
        fontWeight: 400,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
    <span
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      }}
    >
      {value ?? '—'}
    </span>
    <ChevronDown
      size={14}
      strokeWidth={1.75}
      color="var(--shell-text-tertiary)"
      style={{ flexShrink: 0, marginLeft: 'auto' }}
    />
  </button>
);

type ListProps<T> = {
  items: T[];
  selectedId: string | number | null | undefined;
  getId: (item: T) => string | number | undefined;
  getLabel: (item: T) => string;
  onSelect: (item: T) => void;
  emptyText: string;
  searchPlaceholder: string;
};

function PopoverList<T>({
  items,
  selectedId,
  getId,
  getLabel,
  onSelect,
  emptyText,
  searchPlaceholder,
}: ListProps<T>) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((i) => getLabel(i).toLowerCase().includes(q));
  }, [items, query, getLabel]);

  return (
    <div style={{ width: 280, padding: 6 }}>
      <div style={{ padding: '4px 4px 6px' }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={14} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: 13,
              backgroundColor: 'var(--shell-bg-subtle)',
              '& fieldset': { borderColor: 'var(--shell-border-subtle)' },
              '&:hover fieldset': { borderColor: 'var(--shell-border-default)' },
              '&.Mui-focused fieldset': { borderColor: 'var(--shell-accent-primary)' },
            },
            '& .MuiOutlinedInput-input': { padding: '6px 8px' },
          }}
        />
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div
            style={{
              padding: '12px 10px',
              fontSize: 12,
              color: 'var(--shell-text-tertiary)',
              textAlign: 'center',
            }}
          >
            {emptyText}
          </div>
        ) : (
          filtered.map((item) => {
            const id = getId(item);
            const selected = String(id) === String(selectedId);
            return (
              <button
                key={String(id)}
                type="button"
                onClick={() => onSelect(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  height: 30,
                  padding: '0 8px',
                  border: 'none',
                  background: selected ? 'var(--shell-bg-selected)' : 'transparent',
                  borderRadius: 'var(--shell-radius-sm)',
                  cursor: 'pointer',
                  color: 'var(--shell-text-primary)',
                  fontSize: 13,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  if (!selected)
                    (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-hover)';
                }}
                onMouseLeave={(e) => {
                  if (!selected) (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getLabel(item)}
                </span>
                {selected && (
                  <Check size={14} strokeWidth={2} color="var(--shell-accent-primary)" />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

const ContextSwitchers = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const projects: Project[] = useSelector((state: any) => state.reducer.projects);
  const currentProjectId = useSelector((state: any) => state.reducer.project?.id);
  const currentRole: Role | null = useSelector((state: any) => state.reducer.role);
  const user: User = useSelector(selectUser);
  const availableRoles = useMemo(() => user?.roles || [], [user?.roles]);

  const sortedProjects = useMemo(
    () => [...(projects || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru')),
    [projects]
  );

  const currentProject = useMemo(() => {
    if (!currentProjectId || !projects?.length) return null;
    return projects.find((p) => String(p.id) === String(currentProjectId)) ?? null;
  }, [projects, currentProjectId]);

  useEffect(() => {
    if (availableRoles.length > 0 && currentRole === null) {
      dispatch(setCurrRole(availableRoles[0]));
    }
  }, [availableRoles, currentRole, dispatch]);

  const projectAnchor = useRef<HTMLButtonElement | null>(null);
  const roleAnchor = useRef<HTMLButtonElement | null>(null);
  const [openProject, setOpenProject] = useState(false);
  const [openRole, setOpenRole] = useState(false);

  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
      <button
        type="button"
        ref={projectAnchor}
        onClick={() => setOpenProject(true)}
        style={{ all: 'unset' }}
      />
      <Pill
        icon={<Folder size={14} strokeWidth={1.75} />}
        label="Проект"
        value={currentProject?.name ?? null}
        active={openProject}
        onClick={(e) => {
          projectAnchor.current = e.currentTarget;
          setOpenProject(true);
        }}
      />
      <Popover
        open={openProject}
        anchorEl={projectAnchor.current}
        onClose={() => setOpenProject(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              border: '1px solid var(--shell-border-subtle)',
              boxShadow: 'var(--shell-shadow-overlay)',
              borderRadius: 'var(--shell-radius-lg)',
              background: 'var(--shell-bg-surface)',
            },
          },
        }}
      >
        <PopoverList<Project>
          items={sortedProjects}
          selectedId={currentProjectId}
          getId={(p) => p.id}
          getLabel={(p) => p.name || ''}
          onSelect={(p) => {
            const id = String(p.id);
            dispatch(setCurrProject({ id }));
            try {
              localStorage.setItem(PROJECT_STORAGE_KEY, id);
            } catch {
              /* ignore */
            }
            setOpenProject(false);
          }}
          emptyText="Нет проектов"
          searchPlaceholder="Поиск проекта…"
        />
      </Popover>

      <Pill
        icon={<Shield size={14} strokeWidth={1.75} />}
        label="Роль"
        value={currentRole?.title ?? null}
        active={openRole}
        disabled={availableRoles.length === 0}
        onClick={(e) => {
          roleAnchor.current = e.currentTarget;
          setOpenRole(true);
        }}
      />
      <Popover
        open={openRole}
        anchorEl={roleAnchor.current}
        onClose={() => setOpenRole(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              border: '1px solid var(--shell-border-subtle)',
              boxShadow: 'var(--shell-shadow-overlay)',
              borderRadius: 'var(--shell-radius-lg)',
              background: 'var(--shell-bg-surface)',
            },
          },
        }}
      >
        <PopoverList<Role>
          items={availableRoles}
          selectedId={currentRole?.id}
          getId={(r) => r.id}
          getLabel={(r) => r.title || ''}
          onSelect={(r) => {
            dispatch(setCurrRole(r));
            try {
              localStorage.setItem('selected_role_id', String(r.id));
            } catch {
              /* ignore */
            }
            setOpenRole(false);
            navigate('/chat');
          }}
          emptyText="Нет доступных ролей"
          searchPlaceholder="Поиск роли…"
        />
      </Popover>
    </div>
  );
});

ContextSwitchers.displayName = 'ContextSwitchers';

export default ContextSwitchers;
